import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fmtDate } from "@/lib/api";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Modal, Row, Field, Select } from "@/pages/Notizie";
import { toast } from "sonner";
import { LongTextarea } from "@/components/LongText";
import { Plus, LifeBuoy, Clock, User, Link2, ChevronRight, AlertTriangle } from "lucide-react";

export const STATO_META = {
  in_attesa: { label: "Coda – In attesa", cls: "text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30" },
  in_lavorazione: { label: "In lavorazione", cls: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30" },
  attesa_info: { label: "In attesa di informazioni", cls: "text-violet-600 bg-violet-500/10 border-violet-500/30" },
  completato: { label: "Completato", cls: "text-emerald-600 bg-emerald-400/10 border-emerald-400/30" },
  chiuso: { label: "Chiuso", cls: "text-slate-500 bg-slate-100 border-slate-300" },
};
export const PRIO_META = {
  bassa: { label: "Bassa", cls: "text-slate-500 bg-slate-100 border-slate-300" },
  media: { label: "Media", cls: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30" },
  alta: { label: "Alta", cls: "text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30" },
  urgente: { label: "Urgente", cls: "text-red-600 bg-red-100 border-red-300" },
};
const COLL_OPTS = [["", "— Nessun collegamento —"], ["immobile", "Immobile"], ["cliente", "Cliente"], ["pratica", "Pratica / Altro"]];
const PRIO_OPTS = [["bassa", "Bassa"], ["media", "Media"], ["alta", "Alta"], ["urgente", "Urgente"]];

export default function Tickets() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isSeg = user?.role === "segreteria";
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(false);

  const load = () => api.get("/tickets").then((r) => setItems(r.data)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  if (!items) return <div className="skeleton h-96 rounded-2xl" />;

  const attive = items.filter((t) => t.stato !== "chiuso");
  const chiuse = items.filter((t) => t.stato === "chiuso");

  return (
    <div className="fade-up" data-testid="tickets-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle sub={isSeg ? "Tutte le richieste operative ricevute, in coda per data di apertura." : "Le tue richieste alla Segreteria: stato, posizione in coda e messaggi."}>
          {isSeg ? "Coda Ticket" : "Ticket Segreteria"}
        </SectionTitle>
        {!isSeg && (
          <button data-testid="new-ticket-btn" onClick={() => setModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink transition-all shrink-0">
            <Plus size={16} /> Nuovo ticket
          </button>
        )}
      </div>

      <div className="mt-5 space-y-3" data-testid="tickets-active-list">
        {attive.length === 0 ? (
          <div className="card-immo p-12 text-center text-sm text-[var(--muted)]" data-testid="tickets-empty">
            <LifeBuoy size={30} className="mx-auto text-[var(--muted)] mb-3" />
            {isSeg ? "Nessun ticket in coda." : "Non hai ticket aperti. Apri una richiesta alla Segreteria."}
          </div>
        ) : attive.map((t) => <TicketRow key={t.id} t={t} isSeg={isSeg} onOpen={() => nav(`/ticket/${t.id}`)} />)}
      </div>

      {chiuse.length > 0 && (
        <>
          <div className="mt-8 mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--muted)]">Storico chiusi ({chiuse.length})</div>
          <div className="space-y-3 opacity-80" data-testid="tickets-closed-list">
            {chiuse.map((t) => <TicketRow key={t.id} t={t} isSeg={isSeg} onOpen={() => nav(`/ticket/${t.id}`)} />)}
          </div>
        </>
      )}

      {modal && <CreateModal onClose={() => setModal(false)} onSaved={(id) => { setModal(false); load(); if (id) nav(`/ticket/${id}`); }} />}
    </div>
  );
}

function TicketRow({ t, isSeg, onOpen }) {
  const sm = STATO_META[t.stato] || {};
  const pm = PRIO_META[t.priorita] || {};
  return (
    <div data-testid={`ticket-row-${t.id}`} onClick={onOpen}
      className="card-immo p-4 flex items-center gap-3 cursor-pointer hover:-translate-y-0.5 transition-transform">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate flex items-center gap-2">
          {t.priorita === "urgente" && <AlertTriangle size={14} className="text-red-500 shrink-0" />}
          {t.titolo}
        </div>
        <div className="text-[11px] text-[var(--muted)] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
          <span className="flex items-center gap-1"><Clock size={11} />{fmtDate(t.created_at)}</span>
          {t.categoria_label && <span className="flex items-center gap-1 font-medium">{t.categoria_label}</span>}
          {isSeg && <span className="flex items-center gap-1"><User size={11} />{t.richiedente_nome || "—"}</span>}
          {t.collegamento?.label && <span className="flex items-center gap-1 min-w-0"><Link2 size={11} /><span className="truncate">{t.collegamento.label}</span></span>}
          {t.stato === "in_attesa" && t.queue_position && <span className="font-semibold text-[#FFB020]">Posizione in coda: {t.queue_position}</span>}
        </div>
      </div>
      {t.semaforo && t.semaforo !== "chiuso" && (
        <span data-testid={`ticket-sem-${t.id}`}
          title={t.semaforo === "rosso" ? "Oltre il tempo di risposta" : t.semaforo === "giallo" ? "In scadenza" : "Nei tempi"}
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${t.semaforo === "rosso" ? "bg-red-50 text-red-600 border-red-200" : t.semaforo === "giallo" ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-emerald-50 text-emerald-600 border-emerald-200"}`}>
          <span className={`w-2 h-2 rounded-full ${t.semaforo === "rosso" ? "bg-red-500" : t.semaforo === "giallo" ? "bg-amber-500" : "bg-emerald-500"}`} />
          {t.semaforo === "rosso" ? (t.sla_oltre_doppio ? "Oltre 2×" : "Oltre tempo") : t.semaforo === "giallo" ? "In scadenza" : "In tempo"}
        </span>
      )}
      <span data-testid={`ticket-prio-${t.id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border shrink-0 ${pm.cls}`}>{pm.label}</span>
      <span data-testid={`ticket-stato-${t.id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border shrink-0 ${sm.cls}`}>{sm.label}</span>
      <ChevronRight size={16} className="text-[var(--muted)] shrink-0" />
    </div>
  );
}

function SchedaChiamata({ f, setF, errors, clear }) {
  const [lista, setLista] = useState([]);
  useEffect(() => {
    const nome = (c) => [c.nome, c.cognome].filter(Boolean).join(" ") || c.proprietario_nome || c.ragione_sociale || "";
    if (f.collegamento_tipo === "immobile") api.get("/immobili").then((r) => setLista((r.data || []).map((i) => ({ id: i.id, label: i.titolo || i.indirizzo || "Immobile", nome: [i.proprietario_nome, i.proprietario_cognome].filter(Boolean).join(" "), tel: i.telefono || i.proprietario_telefono || "" })))).catch(() => setLista([]));
    else if (f.collegamento_tipo === "cliente") Promise.all([api.get("/proprietari").catch(() => ({ data: [] })), api.get("/acquirenti").catch(() => ({ data: [] }))])
      .then(([p, a]) => setLista([...(p.data || []).map((c) => ({ id: c.id, label: `Venditore · ${nome(c)}`, nome: nome(c), tel: c.telefono || "" })), ...(a.data || []).map((c) => ({ id: c.id, label: `Acquirente · ${nome(c)}`, nome: nome(c), tel: c.telefono || "" }))]));
    else setLista([]);
    setF((p) => (p.collegamento_id ? { ...p, collegamento_id: "", collegamento_label: "", chiamata_nome: "", chiamata_telefono: "" } : p));
  }, [f.collegamento_tipo]);
  const pick = (id) => {
    const s = lista.find((x) => x.id === id);
    if (!s) { setF((p) => ({ ...p, collegamento_id: "", chiamata_nome: "", chiamata_telefono: "" })); return; }
    setF((p) => ({ ...p, collegamento_id: s.id, collegamento_label: s.label, chiamata_nome: s.nome || "", chiamata_telefono: s.tel || "" }));
    clear("chiamata_nome"); clear("chiamata_telefono");
  };
  const set = (k) => (e) => { const v = e.target.value; setF((p) => ({ ...p, [k]: v })); clear(k); };
  return (
    <>
      {lista.length > 0 && <Select label="Scheda collegata (precompila nome e telefono)" testid="ticket-coll-scheda" value={f.collegamento_id || ""} onChange={(e) => pick(e.target.value)} opts={[["", "— Scegli la scheda —"], ...lista.map((x) => [x.id, x.label])]} />}
      <Row>
        <Field label="Nome da chiamare" testid="ticket-chiamata-nome" required error={errors.chiamata_nome} value={f.chiamata_nome} onChange={set("chiamata_nome")} />
        <Field label="Telefono" testid="ticket-chiamata-telefono" type="tel" required error={errors.chiamata_telefono} value={f.chiamata_telefono} onChange={set("chiamata_telefono")} />
      </Row>
    </>
  );
}

function CreateModal({ onClose, onSaved }) {
  const [f, setF] = useState({ titolo: "", descrizione: "", priorita: "media", categoria: "altro", collegamento_tipo: "", collegamento_label: "", chiamata_nome: "", chiamata_telefono: "" });
  const [saving, setSaving] = useState(false);
  const { errors, setErrors, clear, validate, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };
  const chiamata = f.categoria === "richiesta_chiamata";
  const save = async () => {
    const errs = validate({ titolo: [V.required], descrizione: [V.required], ...(chiamata ? { chiamata_nome: [V.required], chiamata_telefono: [V.required] } : {}) }, f);
    if (chiamata && f.chiamata_telefono && String(f.chiamata_telefono).replace(/\D/g, "").length < 6) { errs.chiamata_telefono = "Telefono non valido"; setErrors(errs); }
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    setSaving(true);
    try {
      const { data } = await api.post("/tickets", f);
      toast.success("Ticket inviato alla Segreteria");
      onSaved(data.id);
    } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Nuovo ticket alla Segreteria" onClose={onClose} onSave={save} saving={saving} testid="ticket-modal" saveLabel="Invia" summary={summary}>
      <Field label="Titolo della richiesta" testid="ticket-titolo" required error={errors.titolo} value={f.titolo} onChange={set("titolo")} />
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Descrizione completa<span className="text-red-600 ml-0.5">*</span></label>
        <LongTextarea data-testid="ticket-descrizione" rows={4} value={f.descrizione} onChange={set("descrizione")}
          className={"w-full px-3 py-2.5 rounded-lg bg-white border outline-none text-sm " + (errors.descrizione ? "border-red-500 ring-1 ring-red-500/30" : "border-[var(--border)] focus:border-[#FC82ED]")} />
        {errors.descrizione && <p data-testid="ticket-descrizione-error" className="mt-1 text-xs text-red-600 font-medium">{errors.descrizione}</p>}
      </div>
      <Row>
        <Select label="Categoria (tempo di risposta)" testid="ticket-categoria" value={f.categoria} onChange={set("categoria")} opts={[
          ["urgente_appuntamento", "Urgente appuntamento (4h)"],
          ["documenti", "Documenti (24h)"],
          ["agenda", "Agenda (24h)"],
          ["pubblicazione", "Pubblicazione (48h)"],
          ["richiesta_chiamata", "Richiesta chiamata alla Segreteria (24h)"],
          ["altro", "Altro (48h)"],
        ]} />
        <Select label="Priorità" testid="ticket-priorita" value={f.priorita} onChange={set("priorita")} opts={PRIO_OPTS} />
      </Row>
      <Row>
        <Select label="Collega a" testid="ticket-coll-tipo" value={f.collegamento_tipo} onChange={set("collegamento_tipo")} opts={COLL_OPTS} />
      </Row>
      {f.collegamento_tipo && (
        <Field label="Immobile / cliente / pratica collegata" testid="ticket-coll-label" value={f.collegamento_label} onChange={set("collegamento_label")} />
      )}
      {chiamata && <SchedaChiamata f={f} setF={setF} errors={errors} clear={clear} />}
      <div className="text-[11px] text-[var(--muted)]">Potrai aggiungere allegati e messaggi dopo l'apertura del ticket.</div>
    </Modal>
  );
}
